import React, { ReactElement, useMemo } from 'react'
import { ExamResult, User, UserStats } from '@yaak/components/services/api/api'
import style from './style.less'
import Typography from '../Typography'
import { TypographySizes, TypographyStyles, TypographyTypes } from '../Typography/Typography'
import { toHoursAndMinutes } from '@yaak/ds-admin/src/helpers/helpers'
import Badge from '../Badge'
import { BadgeType } from '../Badge/Badge'
import { FormattedMessage } from 'react-intl'
import { ProgressReportResponse } from "../../services/api/elearningapi"
import ElearningProgress from "./ElearningProgress"

export type StudentsPerformanceVariant = 'default' | 'embedded'

export interface StudentsPerformanceProps {
  user: User
  token: string
  userStats: UserStats
  elearningProgress: ProgressReportResponse
  dsAdmin?: boolean
  selectedLicense?: string
  /** Embedded hides the duplicate page title and uses flatter section chrome (ds-admin overview). */
  variant?: StudentsPerformanceVariant
}

interface ExamResultProps {
  examResult: ExamResult
  dsAdmin?: boolean
}

const formatTopicCategoryLabel = (categoryKey: string): string =>
  categoryKey
    .toLowerCase()
    .split(/[_\s]+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')

const ExamResultComponent: React.FunctionComponent<ExamResultProps> = ({
  examResult,
  dsAdmin = false,
}): ReactElement => {
  const parsedDate = new Date(Date.parse(examResult.timestamp))
  return (
    <div className={style.examRow}>
      <Typography type={TypographyTypes.body}>
        {parsedDate.toLocaleDateString('en', {
          day: 'numeric',
          month: 'long',
          year: 'numeric',
        })}
      </Typography>
      {examResult.passed ? (
        <Badge
          label={
            dsAdmin ? (
              <FormattedMessage id={'add_exam_dialog.passed'} />
            ) : (
              'Passed'
            )
          }
        />
      ) : (
        <Badge
          type={BadgeType.red}
          label={
            dsAdmin ? (
              <FormattedMessage id={'add_exam_dialog.failed'} />
            ) : (
              'Failed'
            )
          }
        />
      )}
    </div>
  )
}

const StudentsPerformance: React.FunctionComponent<
  StudentsPerformanceProps
> = ({
  user,
  token: _token,
  userStats,
  elearningProgress,
  dsAdmin = false,
  selectedLicense,
  variant = 'default',
}): ReactElement => {
    const licenseCode = useMemo(() => {
      return user.licenses?.find((license) => license.licenseId === selectedLicense)?.licenseCode
    }, [user.licenses, selectedLicense])

    const examResults = useMemo(() => {
      if (!user || !licenseCode || !user.examResults) {
        return []
      }

      if (typeof user.examResults === 'object' && !Array.isArray(user.examResults) && !(user.examResults instanceof Map)) {
        const resultsObj = user.examResults as { [key: string]: ExamResult[] }
        return resultsObj[licenseCode] || []
      }

      return []
    }, [user, licenseCode])

    const licenseSpecificData = useMemo(() => {
      if (userStats.licenseRating && licenseCode) {
        const licenseData = userStats.licenseRating[licenseCode]
        if (licenseData) {
          return {
            rating: licenseData.rating?.rating,
            durationMinutes: licenseData.rating?.duration
          }
        }
      }

      return {
        rating: user.rating,
        durationMinutes: user.durationMinutes
      }
    }, [userStats.licenseRating, licenseCode, user.rating, user.durationMinutes])

    const licenseTopicCategoryRatings = useMemo(() => {
      if (userStats.licenseRating && licenseCode) {
        const licenseData = userStats.licenseRating[licenseCode]
        if (licenseData?.lessonTopicCategoryRatings) {
          return licenseData.lessonTopicCategoryRatings
        }
      }

      return null
    }, [userStats.licenseRating, licenseCode, userStats])

    const topicCategoryEntries = useMemo(() => {
      if (!licenseTopicCategoryRatings) {
        return []
      }
      return Object.entries(licenseTopicCategoryRatings).sort(([a], [b]) =>
        a.localeCompare(b)
      )
    }, [licenseTopicCategoryRatings])

    const hasSelectedLicense = useMemo(() => {
      if (!user.licenses || user.licenses.length === 0) {
        return false
      }

      return user.licenses.some((license) => license.licenseId === selectedLicense)
    }, [user.licenses, selectedLicense])

    const showTitle = variant !== 'embedded'
    const theoryFlat = variant === 'embedded'

    if (!hasSelectedLicense) {
      return (
        <div className={style.performance}>
          <Typography type={TypographyTypes.label} styles={TypographyStyles.bold}>
            {dsAdmin ? (
              <FormattedMessage id={'student_performance'} />
            ) : (
              'Student performance'
            )}
          </Typography>
          <div className={style.noLicense}>
            <FormattedMessage id={'no_license_data'} defaultMessage="No data available for this license" />
          </div>
        </div>
      )
    }

    const examSection = (
      <section className={style.section}>
        <Typography className={style.sectionLabel} type={TypographyTypes.label}>
          {dsAdmin ? (
            <FormattedMessage id={'exam_results'} />
          ) : (
            'Exam results'
          )}
        </Typography>
        <div className={style.examList}>
          {examResults && examResults.length > 0 ? (
            examResults.map((examResult: ExamResult, index: number) => (
              <ExamResultComponent
                examResult={examResult}
                dsAdmin={dsAdmin}
                key={examResult.timestamp || index}
              />
            ))
          ) : (
            <div className={style.noExams}>
              <FormattedMessage
                id={'no_exam_results_for_license'}
                defaultMessage="No exam results for this license"
              />
            </div>
          )}
        </div>
      </section>
    )

    const metricsSection = (
      <section className={style.section}>
        <Typography type={TypographyTypes.headline} size={TypographySizes.small}>
          {dsAdmin ? (
            <FormattedMessage
              id={'rating_section_heading'}
              defaultMessage="Rating"
            />
          ) : (
            'Rating'
          )}
        </Typography>
        <div className={style.metricsStrip}>
          <div className={style.metricItem}>
            <Typography className={style.metricLabel} type={TypographyTypes.label}>
              {dsAdmin ? <FormattedMessage id={'score'} /> : 'Score'}
            </Typography>
            <Typography type={TypographyTypes.headline} size={TypographySizes.small}>
              {licenseSpecificData.rating?.toFixed(1) || '-'}
            </Typography>
          </div>
          <div className={style.metricItem}>
            <Typography className={style.metricLabel} type={TypographyTypes.label}>
              {dsAdmin ? <FormattedMessage id={'hours'} /> : 'Hours'}
            </Typography>
            <Typography type={TypographyTypes.headline} size={TypographySizes.small}>
              {licenseSpecificData.durationMinutes
                ? toHoursAndMinutes(licenseSpecificData.durationMinutes)
                : '-'}
            </Typography>
          </div>
        </div>
      </section>
    )

    const topicSection = (
      <section className={style.section}>
        <Typography type={TypographyTypes.headline} size={TypographySizes.small}>
          <FormattedMessage id={'topic_progress'} />
        </Typography>
        <div className={style.topicProgressWrap}>
          {topicCategoryEntries.length > 0 ? (
            topicCategoryEntries.map(([category, data]) => (
              <div className={style.topicCategoryRow} key={category}>
                <Typography type={TypographyTypes.body}>
                  {formatTopicCategoryLabel(category)}
                </Typography>
                <div className={style.topicCategoryMeta}>
                  <Typography type={TypographyTypes.body}>
                    {typeof data.rating === 'number' ? data.rating.toFixed(1) : '-'}
                  </Typography>
                  <Typography className={style.topicCategoryDuration} type={TypographyTypes.label}>
                    {data.durationMinutes
                      ? toHoursAndMinutes(data.durationMinutes)
                      : '-'}
                  </Typography>
                </div>
              </div>
            ))
          ) : (
            <div className={style.noLicense}>
              <FormattedMessage
                id={'no_license_data'}
                defaultMessage="No driving progress data available for this license"
              />
            </div>
          )}
        </div>
      </section>
    )

    const theoryBlock =
      elearningProgress ? (
        <ElearningProgress
          progress={elearningProgress}
          showLeadingDivider={false}
          flat={theoryFlat}
        />
      ) : null

    return (
      <div
        className={
          variant === 'embedded'
            ? `${style.performance} ${style.performanceEmbedded}`
            : style.performance
        }
      >
        {showTitle ? (
          <Typography type={TypographyTypes.label} styles={TypographyStyles.bold}>
            {dsAdmin ? (
              <FormattedMessage id={'student_performance'} />
            ) : (
              'Student performance'
            )}
          </Typography>
        ) : null}

        {variant === 'embedded' ? (
          <div className={style.embeddedGrid}>
            <div className={style.embeddedColumn}>
              {theoryBlock}
              {topicSection}
            </div>
            <div className={style.embeddedColumn}>
              {metricsSection}
              {examSection}
            </div>
          </div>
        ) : (
          <>
            {theoryBlock}
            {examSection}
            {metricsSection}
            {topicSection}
          </>
        )}
      </div>
    )
  }

export default StudentsPerformance
